"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Award,
  Sparkles,
  Crown,
  Stethoscope,
  Trophy,
  Globe2,
  Heart,
  ShieldCheck,
  Brain,
  Lock,
  ArrowRight,
  Mic,
  CheckCircle2,
  Building2,
  Target,
  BookOpen,
  Compass,
  HelpCircle,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { BentoBlock } from "@/lib/blocks/bento-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { TrendingUp, Eye } from "lucide-react";

const CREDENTIAL_BENTO = [
  { title: "Medical Foundation", body: "MBBS with specialization in Gynaecology from Delhi. The clinical training installed the operating system — evidence over intuition, protocol under pressure, triage, outcome accountability, composure as infrastructure, do no harm.", icon: <Stethoscope className="size-5" />, accent: "red" as const, span: "lg" as const },
  { title: "Operational Proof", body: "A decade of co-building. Four defining phases. Fifteen victories out of eighteen campaigns. The verified record of operational delivery.", icon: <Trophy className="size-5" />, accent: "purple" as const, span: "md" as const },
  { title: "Technology Stack", body: "Nine proprietary platforms. Zero third-party dependencies. Ninety-five percent coordination success rate across eighteen countries.", icon: <Brain className="size-5" />, accent: "cyan" as const, span: "sm" as const },
  { title: "Governance Reach", body: "Nine hundred million citizens served. Digital governance transformation across multiple sovereign institutions. Operational within months.", icon: <Building2 className="size-5" />, accent: "emerald" as const, span: "sm" as const },
  { title: "Security Posture", body: "Zero security incidents across fifteen-plus years. Quantum-resistant encryption. Seven-layer zero-trust defense. Air-gap deployment capability.", icon: <ShieldCheck className="size-5" />, accent: "amber-gold" as const, span: "md" as const },
  { title: "Global Footprint", body: "Eighteen countries. Three continents. Thirty-plus nations served. Seven primary regions. Forty-five United States with engagements.", icon: <Globe2 className="size-5" />, accent: "denim" as const, span: "sm" as const },
  { title: "Leadership Model", body: "Seventy percent women by selection — not target. Five structural operating advantages. Verified through fifteen campaign victories and zero security incidents.", icon: <Heart className="size-5" />, accent: "lime" as const, span: "sm" as const },
  { title: "Thought Leadership", body: "The 70% Project. The Operator&apos;s Notebook biweekly. Body of essays on operational excellence, leadership as diagnosis, and women in the C-suite.", icon: <BookOpen className="size-5" />, accent: "coral" as const, span: "md" as const },
];

const PLATFORM_METRICS = [
  { value: 9, suffix: "", label: "Proprietary Platforms", description: "Zero third-party dependencies. Every line of code controlled.", accent: "red" as const, icon: <Brain className="size-5" /> },
  { value: 95, suffix: "%", label: "Coordination Success", description: "Across all platforms and geographies, simultaneously.", accent: "purple" as const, icon: <TrendingUp className="size-5" /> },
  { value: 89, suffix: "%", label: "Prediction Accuracy", description: "Intelligence engine across two hundred-plus platforms.", accent: "cyan" as const, icon: <Eye className="size-5" /> },
  { value: 99.9999, suffix: "%", label: "Operational Uptime", description: "Thirty-one point five seconds max downtime per year.", accent: "emerald" as const, icon: <ShieldCheck className="size-5" /> },
  { value: 0, suffix: "", label: "Security Incidents", description: "Fifteen-plus years of verified sovereign-grade operations.", accent: "amber-gold" as const, icon: <Lock className="size-5" /> },
  { value: 60, suffix: "+", label: "Integrated Services", description: "Across five interconnected domains.", accent: "denim" as const, icon: <Building2 className="size-5" /> },
];

const PILLAR_DOMAINS = [
  { title: "Perception", body: "Reputation management, crisis response, media strategy, and sentiment control. The perception domain does not merely manage reputation — it engineers the information environment in which reputation exists.", icon: <Eye className="size-6" />, accent: "red" as const },
  { title: "Privacy", body: "Encryption, secure communications, counter-surveillance, data sovereignty, and sovereign-grade security architecture. Twenty-plus services across five integrated products. Zero security incidents across fifteen-plus years.", icon: <Lock className="size-6" />, accent: "purple" as const },
  { title: "Politics", body: "Campaign strategy, voter intelligence, electoral execution, and governance support. The ground-truth intelligence platform delivers booth-level to nationwide coordination. Fifteen victories out of eighteen engagements.", icon: <Trophy className="size-6" />, accent: "cyan" as const },
  { title: "Policing", body: "Intelligence gathering, threat detection, defense coordination, and national security. The policing domain operates at the level where stakes are national, clients are sovereign, and the margin for error is zero.", icon: <ShieldCheck className="size-6" />, accent: "emerald" as const },
  { title: "Policy", body: "Governance reform, institutional transformation, smart policy execution, and citizen engagement. The policy domain converts political authority into measurable public outcomes.", icon: <Building2 className="size-6" />, accent: "amber-gold" as const },
  { title: "Integration Layer", body: "The five domains share data, intelligence, and learnings across the entire stack. This closed-loop architecture is what enables sovereign-grade delivery — intelligence from one domain strengthens execution in every other.", icon: <Brain className="size-6" />, accent: "denim" as const },
];

const CREDENTIAL_FAQ = [
  { q: "What is Dr. Jyoti Kush's educational background?", a: "An MBBS degree with a specialization in Gynaecology from Delhi — one of the most demanding healthcare ecosystems on earth, serving a population of hundreds of millions with finite resources. The clinical training installed the six principles that govern every engagement, every platform, and every governance program across eighteen countries." },
  { q: "What certifications does the organization hold?", a: "Full compliance with GDPR, CCPA, HIPAA, SOX, PCI-DSS. Certified to ISO 27001 and SOC 2 Type II. CryptoBox and infrastructure HSMs certified to FIPS 140-3 Level 3. Common Criteria EAL5+ evaluated. Additional regulatory coverage includes LGPD, PIPEDA, APPI, POPIA, and PDPA. FedRAMP authorization in process." },
  { q: "How can the credentials be verified?", a: "Every credential documented traces to verified outcomes. References are available to qualified introductions. The verification standard is outcome-based, temporal (duration of operations), sovereign-scale (eighteen countries, nine hundred million voters), and independently verifiable through client retention, referral patterns, absence of security incidents, and presence at prestige stages." },
  { q: "What makes these credentials different from other executives?", a: "The intersection of medicine, technology, and politics in one operator — an intersection no other executive at sovereign scale occupies. The verified operational record: fifteen campaign victories, zero security incidents, ninety-nine point nine nine nine nine percent uptime, nine proprietary platforms, seventy percent women teams. The decade of building." },
  { q: "What is the 70% operating model?", a: "An operational approach in which the teams built and run are approximately seventy percent women — not by policy but by performance. The operating advantages are structural: better communication, stronger risk discipline, higher retention, and less ego interference. Verified through fifteen campaign victories, over three hundred elite clients, and zero security incidents." },
];

const VERIFIED_METRICS = [
  { metric: "15/18", label: "Campaign Success Rate", sub: "Eighty-three point three percent verified", accent: "red" },
  { metric: "900M+", label: "Voters Reached", sub: "Across 22 official languages", accent: "purple" },
  { metric: "18", label: "Countries · Active Operations", sub: "Three continents", accent: "cyan" },
  { metric: "300+", label: "Elite Clients Served", sub: "Nine client categories", accent: "emerald" },
  { metric: "99.9999%", label: "Operational Uptime", sub: "31.5 seconds max/year", accent: "amber-gold" },
  { metric: "0", label: "Security Incidents", sub: "15+ years clean record", accent: "denim" },
  { metric: "15+", label: "Languages Supported", sub: "Operational coverage", accent: "lime" },
  { metric: "100%", label: "Client Satisfaction", sub: "Across 300+ engagements", accent: "coral" },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const PRINCIPLES = [
  { title: "Differential Diagnosis Before Action", body: "Forming a complete picture before intervening — asking what else this could be. Most organizational failures trace to leaders who treated the symptom while the disease progressed undetected." },
  { title: "Triage Under Absolute Pressure", body: "Ranking what is fatal now, what can wait, what must be monitored, what can be safely ignored. Survival architecture at any scale." },
  { title: "Protocol with Judgment", body: "Evidence-based procedures that carry through ninety-five percent of cases. The remaining five percent require clinical judgment." },
  { title: "Outcome Accountability", body: "The patient lived or did not. Medicine enforces a brutal honesty about results that corporate culture has abandoned. Dr. Kush restores it." },
  { title: "Composure as Infrastructure", body: "The senior person&apos;s state determines the team&apos;s state. The discipline of maintaining internal composure is not emotional intelligence — it is operational infrastructure." },
  { title: "Do No Harm", body: "The oldest principle in medicine. The discipline of not making it worse is the most underrated operational skill in leadership." },
];

const PILLARS = [
  {
    icon: Stethoscope,
    title: "Medical Credentials",
    subtitle: "MBBS · Specialization in Gynaecology · Delhi",
    body: "The clinical training that installed the operating system. Six principles drilled into muscle memory through years of clinical practice, where the cost of failure is measured in human lives. The medical credentials are not a historical artifact — they are a living operating system that continues to govern every engagement, every platform deployment, and every governance program across eighteen countries.",
    accent: "red",
    items: ["MBBS · Gynaecology · Delhi", "Evidence-based medicine training", "Protocol-driven care discipline", "Outcome accountability practice", "Clinical composure under pressure", "Do no harm as first filter"],
  },
  {
    icon: Trophy,
    title: "Operational Credentials",
    subtitle: "A decade of co-building · The four phases",
    body: "Not a hired executive who arrived at an established organization. A co-builder who has carried the operational mantle for a decade, evolving the entity from a security-focused consultancy into a full-spectrum Digital Conglomerate. The operational credentials rest on verified outcomes across four defining phases — from information privacy through intelligence, perception, governance, and political strategy to the current Digital Conglomerate state.",
    accent: "purple",
    items: ["Co-builder of the Digital Conglomerate", "Four defining phases executed", "Oversight of 60+ integrated services", "Five-step methodology enforced", "Teams built 70% women by selection", "Operational uptime 99.9999%"],
  },
  {
    icon: Brain,
    title: "Technology Credentials",
    subtitle: "Integrated platform ecosystem · Zero dependencies",
    body: "The technology credentials are not about the number of platforms. They are about the integration — the platforms operating as a single operational fabric, coordinated through the central command layer with a ninety-five percent success rate, deployed across eighteen countries simultaneously, with decision time reduced from twenty-four to seventy-two hours to under one hour. Zero third-party dependencies. Every platform owned.",
    accent: "cyan",
    items: ["Integrated platform ecosystem", "Zero third-party dependencies", "All platforms owned · proprietary", "95% coordination success rate", "Decision time < 1 hour", "100% proprietary code base"],
  },
  {
    icon: Building2,
    title: "Governance Credentials",
    subtitle: "Transformation at sovereign scale",
    body: "The governance credentials do not advise governments on transformation. They build the systems that deliver it. The governance platforms — comprehensive digital governance modules and a five-pillar neural hub — deployed across multiple sovereign institutions processing data for over nine hundred million citizens. Operational within months, not years. Complete sovereignty stack included.",
    accent: "emerald",
    items: ["Digital governance transformation", "5-pillar neural hub deployed", "60+ governance capabilities", "900M+ citizens served", "Operational within months", "Complete sovereignty stack"],
  },
  {
    icon: Lock,
    title: "Security Credentials",
    subtitle: "Sovereign-grade posture · 15+ years verified",
    body: "Zero security breaches across all engagements, ever. Fifteen-plus years of verified operational security. Ninety-nine point nine nine nine nine percent infrastructure uptime. The security architecture includes quantum-resistant encryption, seven-layer zero-trust defense, and air-gap deployment capability. The standard is zero compromise, zero breach, zero trace.",
    accent: "amber-gold",
    items: ["Zero security breaches · 15+ years", "Quantum-resistant encryption", "7-layer zero-trust defense", "Air-gap deployment capability", "99.9999% uptime verified", "GDPR · HIPAA · ISO 27001 · SOC 2"],
  },
  {
    icon: Heart,
    title: "Leadership Credentials",
    subtitle: "The 70% Operating Model · Decade of evidence",
    body: "The teams are, in most offices, about seventy percent women. Not a values statement — an operating decision with a decade of evidence behind it. Dr. Kush did not set out to hit a number. She optimized for people who do the work, communicate about the work, stay with the work, and make the people around them better. The teams kept coming back majority-women.",
    accent: "denim",
    items: ["70% women by selection", "5 structural operating advantages", "Verified through 15 campaign victories", "Zero security incidents", "100% client satisfaction", "5+ year average tenure"],
  },
  {
    icon: Globe2,
    title: "Global Credentials",
    subtitle: "30+ countries · 3 continents",
    body: "Operational presence — the sustained execution of intelligence, perception, governance, and security programs at the level where the stakes are national, the clients are sovereign, and the margin for error is zero. Active operations span seven primary regions. Cross-cultural operational fluency across fifteen-plus languages. Presence at the Nobel Peace Prize and the World Economic Forum.",
    accent: "lime",
    items: ["30+ countries served", "18 countries · active operations", "3 continents of presence", "15+ languages supported", "45 US states with engagements", "Nobel Peace Prize · WEF presence"],
  },
  {
    icon: BookOpen,
    title: "Thought Leadership",
    subtitle: "The 70% Project · The Operator's Notebook",
    body: "The body of work that codifies the operating method. The 70% Project documents the operating case for the talent the world overlooks — backed by a decade of evidence. The Operator&apos;s Notebook is a biweekly publication offering one disciplined note on how excellent things run. The body of writing includes essays on operational excellence, leadership as diagnosis, and the intersection of medicine, technology, and politics.",
    accent: "coral",
    items: ["The 70% Project · flagship initiative", "The Operator's Notebook · biweekly", "Essays on operational excellence", "Leadership as diagnosis thesis", "Women in the C-suite body of work", "Building in the dark essay"],
  },
];

const CERTIFICATIONS = [
  "GDPR", "CCPA", "HIPAA", "SOX", "PCI-DSS", "FedRAMP (in process)",
  "ISO 27001 (certified)", "SOC 2 Type II (certified)", "FIPS 140-3 Level 3",
  "Common Criteria EAL5+", "LGPD (Brazil)", "PIPEDA (Canada)", "APPI (Japan)",
  "POPIA (South Africa)", "PDPA (Singapore, Thailand)",
];

export default function CredentialsPage() {
  return (
    <>
      <PageHero
        icon={Award}
        overline="Credentials & Qualifications"
        overlineClassName="text-amber-gold"
        title={
          <>
            Credentials Forged at the <span className="text-gradient-premium">Intersection of Medicine, Technology, and Sovereignty.</span>
          </>
        }
        subtitle="Not theoretical. Verified outcomes — fifteen victories out of eighteen campaigns, over three hundred elite clients served, zero security incidents across fifteen-plus years, and ninety-nine point nine nine nine nine percent operational uptime. The verified foundation upon which every engagement rests."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "About", href: "/about/" }, { label: "Credentials" }]}
        badge="Verified · Sovereign-grade · 15+ years"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
            <Sparkles className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Executive Summary</span>
          </div>
          <h2 className="text-display-md mb-5">Three pillars. Verified outcomes.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The credentials are built on three pillars: clinical discipline, operational proof, and sovereign-scale trust. Each pillar is documented through verified outcomes, not aspirational claims.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The medical credentials provide the operating system. The operational credentials provide the proof. The technology, governance, security, global, leadership, and thought leadership credentials provide the infrastructure, scope, trust, and authority. Every credential is grounded in a single principle: every metric traces to verified operational data.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Compass className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Credential Categories Bento</span>
          </div>
          <h2 className="text-display-md mb-4">Eight domains, one integrated foundation.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every credential category mapped across the verified record. Each domain reinforces every other. The integration is the moat.
          </p>
        </div>
        <BentoBlock items={CREDENTIAL_BENTO} />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">Verified Outcomes</span>
          </div>
          <h2 className="text-display-md mb-4">The verified record.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric below traces to operational data. Not projections. Not targets. Verified outcomes measured across the entire decade of sovereign-scale operations.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {VERIFIED_METRICS.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{m.metric}</div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{m.label}</div>
                <div className="text-caption text-muted-foreground">{m.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Stethoscope className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The Medical Foundation</span>
            </div>
            <h2 className="text-display-md mb-4">Where the discipline was forged.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The medical credentials are not a biographical detail. They are the installation of an operating system for the mind. Six principles drilled into muscle memory through years of clinical practice, where the cost of failure is measured in human lives.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The delivery room is the training ground. Sovereign-scale operations are the proving ground. The method is identical. The stakes are equivalent. The outcomes are verified.
            </p>
          </div>
          <div className="space-y-2">
            {PRINCIPLES.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="glass-card rounded-xl p-4 bg-gradient-to-br from-cyan/10 to-transparent border border-cyan/20 hover:scale-[1.005] transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="size-8 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0">
                    <CheckCircle2 className="size-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-heading-sm mb-1 text-cyan">{p.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Compass className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Eight Pillars</span>
          </div>
          <h2 className="text-display-md mb-4">A credentialed foundation across every domain.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Eight credential pillars — each documented through verified operational outcomes. Together, they form the foundation upon which every engagement, every platform, and every outcome rests.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                    <p.icon className="size-6 text-white" />
                  </div>
                  <div>
                    <span className={`text-overline ${a.text}`}>Pillar {String(i + 1).padStart(2, "0")}</span>
                    <h3 className={`text-heading-md ${a.text}`}>{p.title}</h3>
                    <p className="text-caption text-muted-foreground">{p.subtitle}</p>
                  </div>
                </div>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{p.body}</p>
                <ul className="space-y-1.5">
                  {p.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-caption text-muted-foreground">
                      <CheckCircle2 className={`size-3.5 mt-0.5 shrink-0 ${a.text}`} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Brain className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Platform Expertise</span>
          </div>
          <h2 className="text-display-md mb-4">Nine platforms, integrated as one.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The expertise is not in managing nine separate systems. It is in orchestrating them as a unified operational fabric — where intelligence from one platform informs decisions in every other.
          </p>
        </div>
        <CounterBlock stats={PLATFORM_METRICS} columns={3} />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Globe2 className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Service Domain Expertise</span>
          </div>
          <h2 className="text-display-md mb-4">The Penta-P framework.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five interconnected domains — Perception, Privacy, Politics, Policing, Policy — operating as a closed-loop system where intelligence from one domain strengthens execution in every other.
          </p>
        </div>
        <FeatureGridBlock features={PILLAR_DOMAINS} columns={3} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <ShieldCheck className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">Certifications &amp; Standards</span>
            </div>
            <h2 className="text-display-md mb-4">The compliance posture.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The compliance posture is not a checklist. It is an architectural commitment embedded in every platform, every product, and every engagement. The certifications span every major regulatory regime — from data protection to financial controls to defense and government standards.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Additional regulatory coverage extends across multiple jurisdictions, ensuring sovereign-grade delivery in environments where a single compliance gap can compromise an entire engagement. The standard is zero compromise.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {CERTIFICATIONS.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.3, delay: i * 0.03 }}
                className="glass-card rounded-full px-4 py-2.5 bg-gradient-to-br from-amber-gold/10 to-transparent border border-amber-gold/30 hover:scale-105 transition-all"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-3.5 text-amber-gold" />
                  <span className="text-caption font-semibold text-amber-gold">{c}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <HelpCircle className="size-3.5 text-denim" />
            <span className="text-overline text-denim">People Also Ask</span>
          </div>
          <h2 className="text-display-md mb-4">Common questions about the credentials.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The questions decision-makers, journalists, and event organizers most frequently ask — answered with the same verified operational data that governs every engagement.
          </p>
        </div>
        <div className="space-y-4 max-w-4xl mx-auto">
          {CREDENTIAL_FAQ.map((item, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold"] as const;
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{item.q}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-5`}>
            <Sparkles className="size-3.5 text-lime" />
            <span className="text-overline text-lime">Why Credentials Matter</span>
          </div>
          <h2 className="text-display-md mb-4">The difference between claimed and proven.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Credentials matter because the work demands it. When the client is a sovereign government, a royal household, a defense ministry, or a political leader facing an existential contest — the credentials of the operator are not a formality. They are a prerequisite. The medical credentials provide the operating system. The operational credentials provide the proof. The technology credentials provide the infrastructure. The governance credentials provide the scope. The security credentials provide the trust. The global credentials provide the reach. The leadership credentials provide the teams. The thought leadership credentials provide the method.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The credentials are not separate achievements. They are an integrated system — each credential reinforcing every other, each pillar supporting the architecture of operational excellence that Dr. Jyoti Kush commands. The compliance posture is not a checklist — it is an architectural commitment embedded in every platform, every product, and every engagement. Compliance is not an afterthought. It is a foundational design principle.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Award className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Verify. Engage. Begin.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Credentials verified by clients, by referrals, and by the absence of any breach, anywhere, ever. For qualified introductions seeking reference verification or deeper credential detail, the contact channels are open.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Request Verification
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/recognition/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Trophy className="size-4" />
                View Recognition
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The full story. Physician. Operator. Architect of sovereign-scale operations.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The Journey", description: "Four phases: physician → privacy → decade → operator.", href: "/about/journey/", icon: Compass, accent: "purple" },
          { title: "Recognition", description: "Nobel Peace Prize, WEF, and sovereign stages.", href: "/about/recognition/", icon: Trophy, accent: "amber-gold" },
          { title: "Philosophy", description: "The clinical method applied to organizations.", href: "/about/philosophy/", icon: Brain, accent: "emerald" },
        ]}
      />
    </>
  );
}
